import { useAbility } from "@casl/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FC } from "react";

import type { AppAbility } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import { Field, FieldLabel } from "@recobra/ui/field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@recobra/ui/select";

import { caseSummaryQueryOptions } from "../../cases";
import { InlineFormError, sortByLabel } from "../../lib";
import { getErrorMessage } from "../../lib/api";
import { useScopeTaskTypes, useTaskName } from "../task-data";
import { creatableTaskTypes } from "../task-policy";
import { taskFormDefaultValues } from "./TaskFormValues";
import type { TaskCreationFormApi } from "./useTaskCreationForm";

export const TaskTypeSection: FC<{
  form: TaskCreationFormApi;
  taskTypes?: readonly string[];
  contextType: "case" | "client";
  contextId: string;
}> = ({ form, taskTypes, contextType, contextId }) => {
  const ability = useAbility<AppAbility>();
  const queryClient = useQueryClient();
  const caseSummary = useQuery({
    ...caseSummaryQueryOptions(queryClient, contextId),
    enabled: contextType === "case" && !!contextId,
  });
  const scope = contextType === "client" ? "client" : caseSummary.data?.caseType;
  const scopeTaskTypes = useScopeTaskTypes(scope);
  const taskName = useTaskName();
  const selectableTaskTypes = scopeTaskTypes.data
    ? sortByLabel(
        creatableTaskTypes(
          scopeTaskTypes.data,
          undefined,
          ability.can("createPrimary", "Task"),
        ).filter((def) => !taskTypes || taskTypes.includes(def.id)),
        (def) => def.name,
      )
    : [];

  return (
    <form.Subscribe selector={(state) => state.values.taskType}>
      {(taskType) => (
        <Field>
          <FieldLabel htmlFor="task-type">{m.task_field_type()}</FieldLabel>
          <Select
            value={taskType}
            onValueChange={(value) => {
              const newType = value ?? "";
              form.setFieldValue("taskType", newType);
              form.setFieldValue("requestedDocuments", []);
              form.setFieldValue("dvcSearchType", "");
              form.setFieldValue("dvcSearchData", "");

              form.setFieldValue("description", taskFormDefaultValues.description);
              form.setFieldValue("assignedTo", taskFormDefaultValues.assignedTo);
            }}
            disabled={!contextId || !scope || taskTypes?.length === 1}
            itemToStringLabel={(taskType) => (taskType ? taskName(taskType) : "")}
          >
            <SelectTrigger id="task-type" className="w-full">
              <SelectValue placeholder={m.task_field_type_placeholder()} />
            </SelectTrigger>
            <SelectContent>
              {selectableTaskTypes.map((def) => (
                <SelectItem key={def.id} value={def.id}>
                  {def.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {caseSummary.error || scopeTaskTypes.error ? (
            <InlineFormError message={getErrorMessage(caseSummary.error ?? scopeTaskTypes.error)} />
          ) : null}
        </Field>
      )}
    </form.Subscribe>
  );
};
